import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Combobox, type ComboboxOption } from '../Combobox';
import { MultiSelect, type MultiSelectOption } from '../MultiSelect';
import { Select, type SelectOption } from '../Select';
import { FormField } from './FormField';
import {
  asList,
  asText,
  type BaseFieldProps,
  describedBy,
  useBoundField,
  useFieldIds,
} from './fieldBase';

// ——— select / combo / multi ———

export interface SelectFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  options: readonly SelectOption[];
  placeholder?: string;
}

export function SelectField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  options,
  placeholder,
}: SelectFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <Select
        id={ids.id}
        name={field.name}
        value={asText(field.value)}
        onValueChange={field.onChange}
        onBlur={field.onBlur}
        options={options}
        placeholder={placeholder}
        disabled={disabled || field.disabled}
        invalid={Boolean(error)}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}

export interface ComboFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  options: readonly ComboboxOption[];
  placeholder?: string;
}

/** Combo: значение только из справочника; свободный текст → ошибка «Выберите значение из справочника». */
export function ComboField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  options,
  placeholder,
}: ComboFieldProps<TValues>) {
  const { t } = useTranslation('form');
  const ids = useFieldIds();
  const { setError, clearErrors } = useFormContext<TValues>();
  const { field, fieldState } = useBoundField<TValues>(name);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <Combobox
        id={ids.id}
        name={field.name}
        value={asText(field.value)}
        onValueChange={(next) => {
          field.onChange(next);
          if (next) clearErrors(name);
        }}
        onBlur={field.onBlur}
        onUnmatched={() =>
          setError(name, { type: 'dictionary', message: t('combo.pickFromDictionary') })
        }
        options={options}
        placeholder={placeholder}
        disabled={disabled || field.disabled}
        invalid={Boolean(error)}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}

export interface MultiFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  options: readonly MultiSelectOption[];
  placeholder?: string;
  twoColumns?: boolean;
}

export function MultiField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  options,
  placeholder,
  twoColumns,
}: MultiFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      labelId={ids.labelId}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <MultiSelect
        id={ids.id}
        value={asList(field.value)}
        onValueChange={field.onChange}
        onBlur={field.onBlur}
        options={options}
        placeholder={placeholder}
        twoColumns={twoColumns}
        disabled={disabled || field.disabled}
        invalid={Boolean(error)}
        aria-labelledby={ids.labelId}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}
